import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box, CSSObject, CircularProgress, Grid, styled, useMediaQuery, useTheme,
} from '@mui/material';
import { VisualizerDetailsMonthData, VisualizerMonthData } from '../../models';
import { VisualizerService } from '../../services';
import Legend from '../shared/Legend';
import VisualizerDetailsChart from './VisualizerDetailsChart';
import { FeatureContext } from '../../FeatureContext';

const VisualizerDetailsLegend = styled(Legend)(({ theme }) => ({
  marginTop: '12px',
  span: {
    height: '18px',
    width: '18px',
    marginRight: '5px',
  },
  'li:not(:last-child)': {
    marginRight: '24px',
  },
  div: {
    fontSize: '12px',
    display: 'inline',
  },
}));

const VisualizerTextContainer = styled(Box)(({ theme }) => ({
  padding: '8px 64px 8px 83px',
  [theme.breakpoints.down('sm')]: {
    padding: '8px 20px 8px 20px',
  },
}));

const VisualizerPeriodContainer = styled('div')<{ isSmallScreen: boolean }>(({ isSmallScreen, theme }) => {
  if (isSmallScreen) {
    const positionStyles: CSSObject = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    };
    return positionStyles;
  }
  const positionStyles: CSSObject = {
    padding: '8px 64px 8px 83px',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 44px 8px 55px',
    },
  };
  return positionStyles;
});

type Props = {
  month: VisualizerMonthData;
  detailsMonth: VisualizerDetailsMonthData | undefined;
  detailsLoading: boolean;
}

function VisualizerDetailsContainer({ month, detailsMonth, detailsLoading }: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const featureContext = useContext(FeatureContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const legendData = useMemo(() => (VisualizerService.getLegend([month], intl)), [intl, month]);
  const sortedPeriods = useMemo(() => {
    if (detailsMonth) return VisualizerService.sortPeriods(detailsMonth?.periods);
    return undefined;
  }, [detailsMonth]);
  const periodPeakSums = useMemo(() => detailsMonth?.periods.map((period) => {
    const sumObject: any = {};
    for (const [key, value] of period.hours) {
      if (sumObject[key]) {
        sumObject[key] += value;
      } else {
        sumObject[key] = value;
      }
    }
    return Object.entries(sumObject);
  }), [detailsMonth?.periods]);

  return (
    <>
      <VisualizerTextContainer>
        <FormattedMessage id="visualizer-details.p1" />
        {!detailsLoading && <VisualizerDetailsLegend legendItems={legendData} />}
      </VisualizerTextContainer>
      <VisualizerPeriodContainer isSmallScreen={isSmallScreen}>
        {detailsLoading && (
        <Grid
          container
          sx={{ margin: '24px 0' }}
        >
          <Grid
            item
            display="flex"
            justifyContent="center"
            md
            sm
            xs
          >
            <CircularProgress color="primary" size="50px" title="Loading visualizer data..." />
          </Grid>
        </Grid>
        )}
        {sortedPeriods && !detailsLoading && sortedPeriods.map((period, index) => {
          if (featureContext.ENERGY_USAGE_VISUALIZER_DEBUG && periodPeakSums) {
            return (
              <div key={period.label}>
                <ul>
                  {periodPeakSums[index].map((hour) => (
                    <li key={hour[0]}>{`${hour[0]} : ${hour[1]}`}</li>
                  ))}
                </ul>
                <VisualizerDetailsChart key={period.label} detailsMonthPeriod={period} />
              </div>
            );
          }
          return <VisualizerDetailsChart key={period.label} detailsMonthPeriod={period} />;
        })}
      </VisualizerPeriodContainer>
    </>
  );
}

export default VisualizerDetailsContainer;
